import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios'

// APIの応答がキャッシュされるのを防ぐ
Axios.interceptors.request.use((config) => {
  if (typeof config.params === 'undefined') {
    config.params = {}
  }
  if (typeof config.params === 'object') {
    if (typeof URLSearchParams === 'function' && config.params instanceof URLSearchParams) {
      config.params.append('__ts', Date.now().toString())
    } else {
      config.params.__ts = Date.now().toString()
    }
  }
  return config
})

export interface RequestFactorySetting {
  baseUrl: string
  timeout: number
  contentType?: string
  withCredentials: boolean,
  maxRedirects?: number
}

export class RequestFactory {
  constructor(private setting: RequestFactorySetting) {
  }

  getRequest(): GetRequest {
    return this.setUp(new GetRequest())
  }

  postRequest(): PostRequest {
    return this.setUp(new PostRequest())
  }

  putRequest(): PutRequest {
    return this.setUp(new PutRequest())
  }

  patchRequest(): PatchRequest {
    return this.setUp(new PatchRequest())
  }

  deleteRequest(): DeleteRequest {
    return this.setUp(new DeleteRequest())
  }

  private setUp<T extends BaseRequest>(request: T): T {
    request.baseUrl = this.setting.baseUrl
    request.timeout = this.setting.timeout
    request.contentType = this.setting.contentType
    request.withCredentials = this.setting.withCredentials
    request.maxRedirects = this.setting.maxRedirects
    return request
  }
}

export class BaseRequest {
  public baseUrl: string
  public timeout: number
  public contentType?: string
  public withCredentials: boolean = false
  public maxRedirects?: number

  requestConfig(): AxiosRequestConfig {
    let config: AxiosRequestConfig = {
      timeout: this.timeout,
      withCredentials: this.withCredentials,
      maxRedirects: this.maxRedirects
    }
    if (this.contentType) {
      config = Object.assign(config, {
        headers: {
          'content-type': this.contentType
        }
      })
    }
    return config
  }

  withContentType(contentType: string): this {
    this.contentType = contentType
    return this
  }

  contentTypeAuto(): this {
    this.contentType = undefined
    return this
  }
}

/**
 * GET リクエストを送信するもの
 */
export class GetRequest extends BaseRequest {
  /**
   * リクエストパラメータなしのリクエストを送信する
   * @param {string} path - パス
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<U>(path: string): Promise<AxiosResponse<U>>

  /**
   * リクエストパラメータありのリクエストを送信する
   * @param {string} path - パス
   * @param {T} params - リクエストパラメータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, params: T): Promise<AxiosResponse<U>>

  /**
   * リクエストを送信する
   * @param {string} path - パス
   * @param {T} params - リクエストパラメータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, params?: T): Promise<AxiosResponse<U>> {
    const url = this.baseUrl + path
    const config: AxiosRequestConfig = this.requestConfig()
    config.params = params
    return Axios.get(url, config)
  }
}

/**
 * POST リクエストを送信するもの
 */
export class PostRequest extends BaseRequest {
  /**
   * リクエストを送信する
   * @param {string} path - パス
   * @param {T} data - 送信するデータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, data: T): Promise<AxiosResponse<U>> {
    const url = this.baseUrl + path
    const config: AxiosRequestConfig = this.requestConfig()
    return Axios.post(url, data, config)
  }
}

/**
 * PUT リクエストを送信するもの
 */
export class PutRequest extends BaseRequest {
  /**
   * リクエストを送信する
   * @param {string} path - パス
   * @param {T} data - 送信するデータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, data: T): Promise<AxiosResponse<U>> {
    const url = this.baseUrl + path
    const config: AxiosRequestConfig = this.requestConfig()
    return Axios.put(url, data, config)
  }
}

/**
 * PATCH リクエストを送信するもの
 */
export class PatchRequest extends BaseRequest {
  /**
   * リクエストを送信する
   * @param {string} path - パス
   *  * @param {T} data - 送信するデータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, data: T): Promise<AxiosResponse<U>> {
    const url = this.baseUrl + path
    const config: AxiosRequestConfig = this.requestConfig()
    return Axios.patch(url, data, config)
  }
}

/**
 * DELETE リクエストを送信するもの
 */
export class DeleteRequest extends BaseRequest {
  /**
   * リクエストパラメータなしのリクエストを送信する
   * @param {string} path - パス
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<U>(path: string): Promise<AxiosResponse<U>>

  /**
   * リクエストパラメータありのリクエストを送信する
   * @param {string} path - パス
   * @param {T} params - リクエストパラメータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, params: T): Promise<AxiosResponse<U>>

  /**
   * リクエストを送信する
   * @param {string} path - パス
   * @param {T} params - リクエストパラメータ
   * @returns {Promise<AxiosResponse<U>>} - 応答
   */
  execute<T, U>(path: string, params?: T): Promise<AxiosResponse<U>> {
    const url = this.baseUrl + path
    const config: AxiosRequestConfig = this.requestConfig()
    config.params = params
    return Axios.delete(url, config)
  }
}

/**
 * 複数件の情報を取得するリクエスト。
 */
export interface BaseListResponse<T> {
  start: number
  count: number
  total: number
  perPage: number
  items: T[]
}

export type RequestSender<P, R> = (param: P, page: number, perPage: number) => Promise<BaseListResponse<R>>

/**
 * BaseListResponse 形式で応答を返すAPIについて、全件のリクエストを取得するユーティリティクラス。
 */
export class ListItemLoader<P, R> {
  /** APIを呼び出せる最大数 */
  maxCallCount: number = 10
  /** 開始ページ数 */
  fromPage: number = 1
  /** 1ページあたりの取得件数 */
  perPage: number = 10
  /**
   * 呼び出し回数オーバーを無視するオプション。主に開発用なので、本番時に true で読んではいけない。
   * @type {boolean} - 呼び出し回数オーバーを無視する場合、 true
   */
  ignoreCountOver: boolean = false

  /** 現在の呼び出し回数 */
  private callCount: number
  /** 現在のページ数 */
  private currentPage: number
  /** 読出し完了したアイテム */
  private loadedItems: R[]

  constructor(private readonly requestSender: RequestSender<P, R>) {
  }

  load(param: P): Promise<R[]> {
    this.callCount = 0
    this.currentPage = this.fromPage
    this.loadedItems = []

    return new Promise<R[]>((resolve, reject) => {
      this.doRequest(param, resolve, reject)
    })
  }

  private doRequest(param: P, resolve: (itemList: R[]) => void, reject: (reason: any) => void): void {
    this.requestSender(param, this.currentPage, this.perPage)
      .then((response: BaseListResponse<R>) => {
        if (response.items) {
          // 取得したデータを loadedItemsに集約する
          this.loadedItems.push(...response.items)
        }
        if (response.total <= this.loadedItems.length) {
          // 全部取得し終わった
          resolve(this.loadedItems)
          this.clean()
          return
        }
        if (this.callCount <= this.maxCallCount) {
          // まだ取得し終わっていないので、次のリクエストを投げる
          this.callCount++
          this.currentPage++
          this.doRequest(param, resolve, reject)
          return
        }
        // 想定外に件数が多いか、 response.total と実際に取得できた件数の整合性が合わない
        const errorMessage = `最大呼び出し回数を超えました:${this.callCount}`
        if (this.ignoreCountOver) {
          console.error(errorMessage)
          resolve(this.loadedItems)
        }
        reject(errorMessage)
      })
      .catch(reason => {
        reject(reason)
      })
  }

  /**
   * 必要以上にメモリを使い過ぎないよう、余計なデータを削除する。
   */
  private clean(): void {
    this.loadedItems = []
  }
}
